import React, { useRef, useState } from "react";
import { 
	RCCButton, 
	RCCGrid, 
	RCCAlert,
	RCCServicesApi,
	RCCHookToast,
	RCCRefDropzone,
	RCCTooltip
} from "react-common-components";
import { useForm, useWatch } from "react-hook-form";
import { Link } from "react-router-dom";
import Form from "src/components/Form";
import LovDelimiter from "src/components/Lov/Delimiter";
import PageTitle from "src/components/PageTitle";
import Title from "src/components/Title";
import { useTranslation } from "src/context/Translations/context";
import { getValidationErrors, standardErrorMessages } from "src/utils/errors";
import styled, { css } from "styled-components";
import AppIcons from "src/components/AppIcons";
import CheckPermission from "src/components/CheckPermission";
import { PermissionEnum } from "src/context/Auth/interface";
import ListImportFiles from "src/components/ListImportFiles";
import Attachments from "src/components/Attachments";
import importSchema from "./schemas";

const StyledFieldset = styled.fieldset`
		position: relative;
		width: 100%;
		border-radius: 8px;
		border: 1px solid var(--border-color);
		padding: 10px;
	`;
	
const ValuesImport: React.FC = () => { 
	const {translate} = useTranslation();
	const {addToast} = RCCHookToast();
	const [importedFiles,setImportedFiles] = useState();
	const dropZoneRef = useRef<RCCRefDropzone>(null);
	const {
		control,
		formState:{errors},
		setError,
		clearErrors,
		setValue,
		handleSubmit
	}= useForm();

	const delimiterWatch = useWatch({
		control,
		name: "delimiter",
		defaultValue:";"
	});
    
	const onSubmit = async(data:any) => {
		try {
			clearErrors();
			await importSchema(translate).validate({...data},{
				abortEarly: false
			});
			
			const formData = new FormData();
			formData.append("Delimiter", data.delimiter);
			data.dropZone.forEach((attach: any, index: number) => {
				formData.append(`Attachments[${index}]`, attach);
			});

			const response = await RCCServicesApi.postRequest({
				method: "IndicatorsValues/ImportValues",
				contentType: "multipart/form-data",
				body: formData
			});
			if(response.data.length > 0)
				setImportedFiles(() => response.data);

			dropZoneRef.current?.clearFiles();
		} catch (error) {
			if(getValidationErrors(error, setError))
				return;

			standardErrorMessages(error, translate("Erro ao importar arquivos"), addToast);
		}	
		
	};
		
	return (
		<>
			<RCCGrid
				container
				item
			>
				<PageTitle
					title={translate("Importação manual")}
					icon={() => <AppIcons name="indicators" />}
				>
					<Link to="/indicators/valueslog?origin=1">
						<RCCTooltip
							text={translate("Logs de importação")}
						>
							<RCCButton
								icon={{name: "stack", color: "#FFF", size: 18}}
								layout="isRounded"
								size="md"
							/>
						</RCCTooltip>
					</Link>
				</PageTitle>
				
				<RCCGrid container item 
					styleInline={css`&.MuiGrid-root {
						@media (min-width: 960px) {
							flex-wrap: nowrap;
						}
					}`}
				>
					<RCCAlert
						title={`${translate("Documentos delimitados")}!`}
						description={<span>{`${translate("Selecione os arquivos que deseja importar para o sistema")}. ${translate("Podem ser arquivos delimitados TXT ou CSV e planilhas XLS, XLSX ou ODS")}`}.
							<a href="https://connector.effectiveerp.com.br/Manuais/Central/5" target="_blank" rel="noreferrer"><u>{translate("Acesse o manual completo")}.</u></a></span>}
					/>
					<RCCAlert
						title={`${translate("Arquivos compactados")}!`}
						description={`${translate("Para facilitar o envio, compacte os arquivos em extensões .zip ou .rar para enviar")}.`}
						styleInline="@media (min-width: 960px) {
							margin-left:8px
						};"
					/>
					<RCCAlert
						title={`${translate("Máximo 10Mb")}!`}
						description={`${translate("Cada arquivo poderá conter no máximo 10Mb de tamanho")}.`}
						styleInline="@media (min-width: 960px) {
							margin-left:8px
						};"
					/>
				</RCCGrid>
				<Form onSubmit={handleSubmit(onSubmit)}>
					<RCCGrid container 
						justifyContent="center" 
						alignContent="center" 
						alignItems="center"
					>
						<RCCGrid item xs={12} md={8} lg={7}>
							
							<RCCGrid container item>
								<LovDelimiter
									control={control}
									name="delimiter"
									error={errors.delimiter}
								/>
								<StyledFieldset >
									<legend>
										<Title size="xs" color="purple">
											{translate("Posição das colunas")}
										</Title>
									</legend>
									<RCCGrid container justifyContent="center" >
										<Title size="sm" color="defaultColor">
											{translate("Código campo")}
										</Title>
										{delimiterWatch}
										<Title size="sm" color="defaultColor">
											{translate("Código tipo de período")}
										</Title>
										{delimiterWatch}
										<Title size="sm" color="defaultColor">
											{translate("Código périodo")}
										</Title>
										{delimiterWatch}
										<Title size="sm" color="defaultColor">
											{translate("Valor")}
										</Title>
									</RCCGrid>
								</StyledFieldset>
								<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE}>
									<RCCGrid container item 
										styleInline="align-self:flex-end;width:100%;">
										<RCCButton
											icon={{
												name: "upload", 
												color: "#FFF", 
												size: 18
											}}
											size="lg"
											type="submit"
											styleInline="width:100%;margin:10px 0;"
										>{translate("Importar")}</RCCButton>
									</RCCGrid>
								</CheckPermission>
							</RCCGrid>
							<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE}>
								<Attachments 
									name="dropZone"
									setValue={setValue}
									ref={dropZoneRef}
									acceptedFormats=".csv,.txt,.xls,.xlsx,.ods,.rar,.zip"
								/>
							</CheckPermission>
						</RCCGrid>
						<ListImportFiles 
							importedFiles={importedFiles}
						/>
					</RCCGrid>
				</Form>
			</RCCGrid>
		</>
	);
};

export default ValuesImport;
